import type { ReactNode } from 'react';
import { IconChevronRight } from '@tabler/icons-react';
import { Box } from '../../atoms/Box';
import { Group } from '../../atoms/Group';
import { Stack } from '../../atoms/Stack';
import { Text } from '../../atoms/Text';
import { Divider } from '../../atoms/Divider';
import type { CoreProps } from '../../types';
import { useShouldRender } from '../../../hooks/useShouldRender';
import { splitNameInitial } from './splitNameInitial';

export interface MobileListRowProps extends CoreProps {
  title: string;
  subtitle?: string | null;
  imageUrl?: string | null;
  /** Used for initials when no image. Defaults to title. */
  initialsFrom?: string;
  trailing?: ReactNode;
  showChevron?: boolean;
  showDivider?: boolean;
  onClick?: () => void;
}

export function MobileListRow({
  title,
  subtitle,
  imageUrl,
  initialsFrom,
  trailing,
  showChevron = true,
  showDivider = true,
  onClick,
  testId,
  view = 'all',
}: MobileListRowProps) {
  const shouldRender = useShouldRender(view);
  if (!shouldRender) return null;

  const initials = splitNameInitial(initialsFrom || title);
  const trailingNode =
    trailing ??
    (showChevron ? (
      <IconChevronRight size={22} stroke={1.75} color="var(--mantine-color-gray-5)" />
    ) : null);

  return (
    <>
      <Group
        gap="sm"
        wrap="nowrap"
        align="center"
        px="md"
        py="sm"
        data-testid={testId}
        style={onClick ? { cursor: 'pointer' } : undefined}
        onClick={onClick}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ) : (
          <Box
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'var(--mantine-color-gray-2)',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <Text size="sm" fw={600} c="brand">
              {initials}
            </Text>
          </Box>
        )}

        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Text fw={700} size="sm" lineClamp={1} c="dark">
            {title}
          </Text>
          {subtitle ? (
            <Text size="xs" c="dimmed" lineClamp={1}>
              {subtitle}
            </Text>
          ) : null}
        </Stack>

        {trailingNode ? (
          <Box style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{trailingNode}</Box>
        ) : null}
      </Group>
      {showDivider ? <Divider color="gray.2" /> : null}
    </>
  );
}
